import {
  type AnimationState,
  type CaptionPage,
  colorMatrix,
  colorMatrixKey,
  type DeviceElement,
  elementStateAt,
  type GroupElement,
  hasColorMatrix,
  ICON_VIEWBOX,
  type IconElement,
  type ImageElement,
  isCurved,
  isRichText,
  opaqueHex,
  pageSize,
  paintToCss,
  parseHex,
  type QrElement,
  qrLogoArea,
  qrLogoRect,
  qrMatrix,
  qrModulesPath,
  REST_STATE,
  type SceneDocument,
  type SceneElement,
  type SvgElement,
  type TextElement,
  textParagraphs,
  videoStack,
  vignetteCss,
} from "@cva/shared";
import { type CSSProperties, Fragment, type ReactNode, useId } from "react";
import { renderCaptions } from "./captions";
import { renderChart } from "./chart";
import { DeviceParts } from "./device";
import { MaskClip, MaskStroke } from "./mask";
import { renderPath } from "./path";
import { ShapeSvg } from "./shape";
import {
  boxShadowCss,
  boxStyle,
  dropShadowCss,
  joinFilters,
  LIST_MARKER_CSS,
  paragraphStyle,
  textBoxStyle,
  textContentStyle,
  textRunStyle,
} from "./styles";
import { renderTable } from "./table";
import { CurvedText, withHighlight } from "./text-effects";
import { ColorMatrixFilter } from "./video";

/**
 * Renderer v1: turns a scene document into positioned DOM/SVG.
 * A pure function of its props, so the editor, thumbnails and the export
 * worker (headless Chromium) produce identical output. See docs/02-architektur.md.
 * Video documents render at a time `t`: time ranges, animations and captions
 * are functions of it (docs/05-video-und-untertitel.md).
 */

export interface AssetInfo {
  url: string;
  width?: number | null;
  height?: number | null;
}

export type ResolveAsset = (assetId: string) => AssetInfo | string | undefined;

export interface SceneRendererProps {
  document: SceneDocument;
  pageIndex?: number;
  /** Resolves an asset id to its URL (e.g. `/files/...`). */
  resolveAsset?: ResolveAsset;
  /** @deprecated use `resolveAsset` */
  resolveAssetUrl?: (assetId: string) => string | undefined;
  /** Extra area drawn around the page (print bleed) in document pixels. */
  bleed?: number;
  /** Elements that are not drawn (e.g. the text currently edited). */
  hiddenIds?: ReadonlySet<string>;
  /** Clip everything outside the page (+ bleed). Default: true. */
  clip?: boolean;
  /** Video: position on the timeline in ms. Without it, time ranges are ignored. */
  time?: number;
  /** Video: play entrance/exit animations (false: visible elements at rest). Default: true. */
  animate?: boolean;
  /** Video: captions derived from the transcripts. */
  captions?: readonly CaptionPage[];
  /** Editor: sample text shown in captions elements while nothing is spoken. */
  captionPlaceholder?: string;
  /** No page background (transparent video overlay frames). */
  transparent?: boolean;
  /** Drawn above the page background and below the elements (the video picture). */
  underlay?: ReactNode;
  /**
   * Video: what lies between the bands of elements (`videoStack`): entry `i`
   * is drawn right above band `i` – the clips of the tracks above it.
   */
  interlays?: readonly ReactNode[];
  /** Video export: only these top-level elements (one band of `videoStack`). */
  layer?: ReadonlySet<string>;
}

interface RenderContext {
  doc: SceneDocument;
  resolve: (assetId: string) => AssetInfo | undefined;
  hiddenIds?: ReadonlySet<string> | undefined;
  idPrefix: string;
  time: number | undefined;
  animate: boolean;
  captions: readonly CaptionPage[] | undefined;
  captionPlaceholder: string | undefined;
  layer: ReadonlySet<string> | undefined;
  /**
   * Without animations (editing while paused): start of the visible group
   * around the elements. Parts that appear a little later (staggered
   * building blocks) are shown with it, so the whole group can be edited.
   */
  restFrom?: number | undefined;
}

/** Longest entrance delay inside a group that the paused editor shows right away. */
const REST_STAGGER_MS = 2000;

function assetUrl(ctx: RenderContext, assetId: string): string | undefined {
  return ctx.resolve(assetId)?.url;
}

/** Text with a typewriter reveal: hidden letters keep their space, so the layout stays. */
function textContent(text: string, reveal: number | null): ReactNode {
  if (text.length === 0) return "​";
  if (reveal === null) return text;
  const chars = [...text];
  const shown = Math.round(reveal * chars.length);
  return (
    <>
      {chars.slice(0, shown).join("")}
      <span style={{ visibility: "hidden" }}>{chars.slice(shown).join("")}</span>
    </>
  );
}

/** Splits a run for the typewriter reveal: `shown` code points visible, the rest keeps its space. */
function revealed(text: string, shown: number): ReactNode {
  const chars = [...text];
  if (shown >= chars.length) return text;
  return (
    <>
      {chars.slice(0, shown).join("")}
      <span style={{ visibility: "hidden" }}>{chars.slice(shown).join("")}</span>
    </>
  );
}

/**
 * Mixed formatting and lists (scene/richtext.ts): one block per paragraph,
 * one span per formatted run. The inline text editor builds the same DOM.
 */
function richTextContent(element: TextElement, reveal: number | null): ReactNode {
  const paragraphs = textParagraphs(element);
  let remaining =
    reveal === null ? Number.POSITIVE_INFINITY : Math.round(reveal * [...element.text].length);
  return paragraphs.map((paragraph, index) => {
    const started = remaining > 0 || paragraph.text.length === 0;
    const children = paragraph.runs.map((run) => {
      const length = [...run.text].length;
      const content = revealed(run.text, Math.max(0, remaining));
      remaining -= length;
      return (
        <span key={run.from} style={textRunStyle(run.style, element)}>
          {content}
        </span>
      );
    });
    // The line break counts like a letter.
    remaining -= 1;
    return (
      <div
        // biome-ignore lint/suspicious/noArrayIndexKey: paragraphs are positional
        key={index}
        data-list-marker={paragraph.marker && started ? paragraph.marker : undefined}
        style={paragraphStyle(paragraph.format)}
      >
        {children.length > 0 ? withHighlight(element, children) : <br />}
      </div>
    );
  });
}

/** Paragraph blocks are needed for formatting, and to show a trailing empty line. */
function needsParagraphs(element: TextElement): boolean {
  return isRichText(element) || element.text.endsWith("\n");
}

function renderText(element: TextElement, state: AnimationState): ReactNode {
  if (isCurved(element)) {
    return (
      <div key={element.id} data-element-id={element.id} style={textBoxStyle(element, state)}>
        <CurvedText element={element} />
      </div>
    );
  }
  const rich = needsParagraphs(element);
  return (
    <div key={element.id} data-element-id={element.id} style={textBoxStyle(element, state)}>
      {rich && element.paragraphs?.some((format) => format?.list) ? (
        <style>{LIST_MARKER_CSS}</style>
      ) : null}
      <div data-text-content="" style={textContentStyle(element)}>
        {rich
          ? richTextContent(element, state.reveal)
          : withHighlight(element, textContent(element.text, state.reveal))}
      </div>
    </div>
  );
}

function flipTransform(element: { flipX: boolean; flipY: boolean }): string | undefined {
  return element.flipX || element.flipY
    ? `scale(${element.flipX ? -1 : 1}, ${element.flipY ? -1 : 1})`
    : undefined;
}

function imageInnerStyle(element: ImageElement): CSSProperties {
  if (element.crop) {
    const { x, y, width, height } = element.crop;
    return {
      position: "absolute",
      left: `${(-x / width) * 100}%`,
      top: `${(-y / height) * 100}%`,
      width: `${100 / width}%`,
      height: `${100 / height}%`,
      maxWidth: "none",
      display: "block",
    };
  }
  return {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: element.fit,
    display: "block",
  };
}

/**
 * Mask with the image's own alpha at the same place as the image: the
 * vignette only darkens the picture, not the transparent parts of a cut-out.
 */
function imageMaskStyle(element: ImageElement, url: string): CSSProperties {
  let size: string;
  let position: string;
  if (element.crop) {
    const width = element.width / element.crop.width;
    const height = element.height / element.crop.height;
    size = `${width}px ${height}px`;
    position = `${-element.crop.x * width}px ${-element.crop.y * height}px`;
  } else {
    size = element.fit;
    position = "center";
  }
  const image = `url("${url.replace(/"/g, "%22")}")`;
  return {
    maskImage: image,
    maskSize: size,
    maskPosition: position,
    maskRepeat: "no-repeat",
    WebkitMaskImage: image,
    WebkitMaskSize: size,
    WebkitMaskPosition: position,
    WebkitMaskRepeat: "no-repeat",
  };
}

function renderImage(element: ImageElement, ctx: RenderContext, state: AnimationState): ReactNode {
  const url = assetUrl(ctx, element.assetId);
  const mask = element.mask;
  const radius = mask ? undefined : element.radius || undefined;
  const color = element.color;
  const filterId = hasColorMatrix(color) ? `grade-${colorMatrixKey(colorMatrix(color))}` : null;
  const vignette = url ? vignetteCss(color?.vignette ?? 0) : undefined;
  const box = boxStyle(element, state);
  const content = (
    <div
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        borderRadius: radius,
        background: url ? undefined : "#e5e7eb",
        transform: flipTransform(element),
      }}
    >
      {url && filterId && color ? <ColorMatrixFilter id={filterId} color={color} /> : null}
      {url ? (
        <img
          src={url}
          alt=""
          draggable={false}
          decoding="sync"
          style={{
            ...imageInnerStyle(element),
            filter: filterId ? `url(#${filterId})` : undefined,
          }}
        />
      ) : null}
      {url && vignette ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: vignette,
            pointerEvents: "none",
            ...imageMaskStyle(element, url),
          }}
        />
      ) : null}
    </div>
  );
  return (
    <div
      key={element.id}
      data-element-id={element.id}
      style={
        mask
          ? // A masked image casts the shadow of its form.
            {
              ...box,
              filter: joinFilters(box.filter as string | undefined, dropShadowCss(element.shadow)),
            }
          : { ...box, borderRadius: radius, boxShadow: boxShadowCss(element.shadow) }
      }
    >
      {mask ? (
        <MaskClip mask={mask} width={element.width} height={element.height}>
          {content}
        </MaskClip>
      ) : (
        content
      )}
      {mask && element.stroke && element.stroke.width > 0 ? (
        <MaskStroke
          mask={mask}
          width={element.width}
          height={element.height}
          stroke={element.stroke}
        />
      ) : element.stroke && element.stroke.width > 0 ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: radius,
            border: `${element.stroke.width}px ${element.stroke.dash === "solid" ? "solid" : element.stroke.dash} ${element.stroke.color}`,
            boxSizing: "border-box",
            pointerEvents: "none",
          }}
        />
      ) : null}
    </div>
  );
}

/** SVG fill for a hex colour with alpha (fill + fill-opacity). */
function svgPaint(hex: string): { fill: string; fillOpacity?: number } {
  const alpha = parseHex(hex).a;
  return alpha < 0.999 ? { fill: opaqueHex(hex), fillOpacity: alpha } : { fill: opaqueHex(hex) };
}

/**
 * QR code: square, centred in the box, modules as one vector path. A logo sits
 * in the middle as an `<img>` (loaded and colour-converted like other images),
 * the modules under it are left out.
 */
function renderQr(element: QrElement, ctx: RenderContext, state: AnimationState): ReactNode {
  const matrix = qrMatrix(element.value, element.ecc);
  const box = boxStyle(element, state);
  const total = matrix ? matrix.size + 2 * element.margin : 25;
  const background = parseHex(element.background).a > 0 ? svgPaint(element.background) : null;
  const logo = matrix ? element.logo : undefined;
  const logoUrl = logo ? assetUrl(ctx, logo.assetId) : undefined;
  let logoStyle: CSSProperties | null = null;
  if (matrix && logo && logoUrl) {
    // The code is the largest centred square of the box (like `meet` above).
    const side = Math.min(element.width, element.height);
    const unit = side / total;
    const rect = qrLogoRect(matrix, logo, element.margin);
    logoStyle = {
      position: "absolute",
      left: (element.width - side) / 2 + rect.x * unit,
      top: (element.height - side) / 2 + rect.y * unit,
      width: rect.size * unit,
      height: rect.size * unit,
      objectFit: "contain",
      display: "block",
    };
  }
  return (
    <div
      key={element.id}
      data-element-id={element.id}
      style={{
        ...box,
        filter: joinFilters(box.filter as string | undefined, dropShadowCss(element.shadow)),
      }}
    >
      <svg
        viewBox={`0 0 ${total} ${total}`}
        preserveAspectRatio="xMidYMid meet"
        width="100%"
        height="100%"
        style={{ position: "absolute", inset: 0, display: "block", overflow: "visible" }}
        shapeRendering={element.style === "square" ? "crispEdges" : undefined}
        aria-hidden="true"
      >
        {background ? <rect width={total} height={total} {...background} /> : null}
        {matrix ? (
          <path
            d={qrModulesPath(
              matrix,
              element.style,
              element.margin,
              logo?.clear ? qrLogoArea(matrix.size, logo) : null,
            )}
            fillRule="evenodd"
            {...svgPaint(element.color)}
          />
        ) : (
          // Content too long: a crossed placeholder (the editor explains why).
          <g stroke="#a1a1aa" strokeWidth={0.6} fill="none">
            <rect
              x={0.3}
              y={0.3}
              width={total - 0.6}
              height={total - 0.6}
              strokeDasharray="1.5 1"
            />
            <path d={`M0 0L${total} ${total}M${total} 0L0 ${total}`} />
          </g>
        )}
      </svg>
      {logoStyle ? (
        <img src={logoUrl} alt="" draggable={false} decoding="sync" style={logoStyle} />
      ) : null}
    </div>
  );
}

/** Library icon: filled paths on the 256 grid, centred in the box (aspect ratio kept). */
function renderIcon(element: IconElement, state: AnimationState): ReactNode {
  const box = boxStyle(element, state);
  const paint = svgPaint(element.color);
  const alpha = paint.fillOpacity ?? 1;
  return (
    <div
      key={element.id}
      data-element-id={element.id}
      style={{
        ...box,
        filter: joinFilters(box.filter as string | undefined, dropShadowCss(element.shadow)),
      }}
    >
      <svg
        viewBox={`0 0 ${ICON_VIEWBOX} ${ICON_VIEWBOX}`}
        preserveAspectRatio="xMidYMid meet"
        width="100%"
        height="100%"
        style={{
          position: "absolute",
          inset: 0,
          display: "block",
          overflow: "visible",
          transform: flipTransform(element),
        }}
        aria-hidden="true"
      >
        {element.paths.map((path, index) => {
          const opacity = alpha * (path.opacity ?? 1);
          return (
            <path
              // biome-ignore lint/suspicious/noArrayIndexKey: layers have no identity of their own
              key={index}
              d={path.d}
              fill={paint.fill}
              fillOpacity={opacity < 0.999 ? opacity : undefined}
            />
          );
        })}
      </svg>
    </div>
  );
}

/** Device mockup: frame, sides and the screenshot on the screen (see device.tsx). */
function renderDevice(
  element: DeviceElement,
  ctx: RenderContext,
  state: AnimationState,
): ReactNode {
  const box = boxStyle(element, state);
  const asset = element.assetId ? ctx.resolve(element.assetId) : undefined;
  return (
    <div
      key={element.id}
      data-element-id={element.id}
      style={{
        ...box,
        filter: joinFilters(box.filter as string | undefined, dropShadowCss(element.shadow)),
      }}
    >
      <DeviceParts element={element} asset={asset} idPrefix={ctx.idPrefix} />
    </div>
  );
}

function renderSvg(element: SvgElement, ctx: RenderContext, state: AnimationState): ReactNode {
  const url = assetUrl(ctx, element.assetId);
  const flip = flipTransform(element);
  const box = boxStyle(element, state);
  return (
    <div
      key={element.id}
      data-element-id={element.id}
      style={{
        ...box,
        filter: joinFilters(box.filter as string | undefined, dropShadowCss(element.shadow)),
      }}
    >
      {url ? (
        <img
          src={url}
          alt=""
          draggable={false}
          decoding="sync"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
            transform: flip,
          }}
        />
      ) : null}
    </div>
  );
}

function renderShape(
  element: Extract<SceneElement, { type: "shape" }>,
  ctx: RenderContext,
  state: AnimationState,
) {
  // Rectangles and ellipses get a real box-shadow (stays vector in PDFs);
  // other outlines fall back to a drop-shadow filter.
  const simple = element.shape === "rect" || element.shape === "ellipse";
  const box = boxStyle(element, state);
  return (
    <div
      key={element.id}
      data-element-id={element.id}
      style={{
        ...box,
        borderRadius:
          element.shape === "ellipse" ? "50%" : element.radius ? element.radius : undefined,
        boxShadow: simple ? boxShadowCss(element.shadow) : undefined,
        filter: joinFilters(
          box.filter as string | undefined,
          simple ? undefined : dropShadowCss(element.shadow),
        ),
      }}
    >
      <ShapeSvg element={element} idPrefix={`${ctx.idPrefix}-${element.id}`} />
    </div>
  );
}

function renderGroup(
  element: GroupElement,
  ctx: RenderContext,
  depth: number,
  state: AnimationState,
): ReactNode {
  const inner =
    !ctx.animate && ctx.time !== undefined
      ? { ...ctx, restFrom: ctx.restFrom ?? element.time?.start ?? 0 }
      : ctx;
  return (
    <div
      key={element.id}
      data-element-id={element.id}
      data-group=""
      style={boxStyle(element, state)}
    >
      {renderChildren(element.children, inner, depth + 1)}
    </div>
  );
}

/** Paused editor: a staggered part of a visible group counts as visible already. */
function shownAtRest(element: SceneElement, ctx: RenderContext): boolean {
  const { time, restFrom } = ctx;
  if (ctx.animate || time === undefined || restFrom === undefined || !element.time) return false;
  return (
    time >= restFrom &&
    time < element.time.end &&
    element.time.start > time &&
    element.time.start - restFrom <= REST_STAGGER_MS
  );
}

function renderElement(element: SceneElement, ctx: RenderContext, depth: number): ReactNode {
  if (element.hidden || ctx.hiddenIds?.has(element.id)) return null;
  if (depth === 0 && ctx.layer && !ctx.layer.has(element.id)) return null;
  if (element.type === "captions") {
    return renderCaptions(element, {
      time: ctx.time,
      pages: ctx.captions,
      placeholder: ctx.captionPlaceholder,
    });
  }
  let state = REST_STATE;
  if (ctx.time !== undefined && !shownAtRest(element, ctx)) {
    const current = elementStateAt(element, ctx.time, { animate: ctx.animate });
    if (!current) return null;
    state = current;
  }
  switch (element.type) {
    case "text":
      return renderText(element, state);
    case "image":
      return renderImage(element, ctx, state);
    case "svg":
      return renderSvg(element, ctx, state);
    case "qr":
      return renderQr(element, ctx, state);
    case "icon":
      return renderIcon(element, state);
    case "device":
      return renderDevice(element, ctx, state);
    case "path":
      return renderPath(element, state);
    case "chart":
      return renderChart(element, state);
    case "shape":
      return renderShape(element, ctx, state);
    case "group":
      return renderGroup(element, ctx, depth, state);
    case "table":
      return renderTable(
        element,
        state,
        joinFilters(
          boxStyle(element, state).filter as string | undefined,
          dropShadowCss(element.shadow),
        ),
      );
  }
}

function renderChildren(ids: string[], ctx: RenderContext, depth: number): ReactNode[] {
  if (depth > 50) return [];
  return ids.map((id) => {
    const element = ctx.doc.elements[id];
    return element ? renderElement(element, ctx, depth) : null;
  });
}

/**
 * Video pages: the elements band by band in the order of the tracks
 * (`videoStack`), between them what the tracks above hold (`interlays`) –
 * the order of the export.
 */
function renderVideoLayers(
  doc: SceneDocument,
  ctx: RenderContext,
  interlays: readonly ReactNode[] | undefined,
): ReactNode {
  const { bands } = videoStack(doc);
  return bands.map((band, index) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: the hosts of the interlays must stay mounted
    <Fragment key={index}>
      {renderChildren(band.ids, ctx, 0)}
      {interlays?.[index] ? (
        <div
          data-interlay={index}
          style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}
        >
          {interlays[index]}
        </div>
      ) : null}
    </Fragment>
  ));
}

function makeResolver(props: SceneRendererProps): (assetId: string) => AssetInfo | undefined {
  return (assetId) => {
    if (props.resolveAsset) {
      const result = props.resolveAsset(assetId);
      return typeof result === "string" ? { url: result } : result;
    }
    const url = props.resolveAssetUrl?.(assetId);
    return url ? { url } : undefined;
  };
}

/**
 * Renders one page at its native size (CSS pixels). With `bleed`, the output
 * grows by that amount on every side and the page origin moves accordingly.
 */
export function SceneRenderer(props: SceneRendererProps) {
  const { document: doc, pageIndex = 0, bleed = 0, clip = true } = props;
  const idPrefix = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const page = doc.pages[pageIndex] ?? doc.pages[0];
  if (!page) return null;
  const size = pageSize(doc, page);
  const ctx: RenderContext = {
    doc,
    resolve: makeResolver(props),
    hiddenIds: props.hiddenIds,
    idPrefix,
    time: props.time,
    animate: props.animate ?? true,
    captions: props.captions,
    captionPlaceholder: props.captionPlaceholder,
    layer: props.layer,
  };
  return (
    <div
      data-scene-page={page.id}
      style={{
        position: "relative",
        width: size.width + bleed * 2,
        height: size.height + bleed * 2,
        background: props.transparent ? "transparent" : paintToCss(page.background),
        overflow: clip ? "hidden" : "visible",
      }}
    >
      {props.underlay ? (
        <div
          data-underlay=""
          style={{
            position: "absolute",
            left: bleed,
            top: bleed,
            width: size.width,
            height: size.height,
            overflow: "hidden",
          }}
        >
          {props.underlay}
        </div>
      ) : null}
      <div
        data-page-origin=""
        style={{
          position: "absolute",
          left: bleed,
          top: bleed,
          width: size.width,
          height: size.height,
        }}
      >
        {doc.kind === "video" ? renderVideoLayers(doc, ctx, props.interlays) : null}
        {doc.kind === "video" ? null : renderChildren(page.children, ctx, 0)}
      </div>
    </div>
  );
}

export interface ScenePreviewProps extends SceneRendererProps {
  /** Target width in pixels; the page is scaled proportionally. */
  width: number;
  className?: string;
}

/** Scaled, non-interactive preview (document lists, page strip, viewer). */
export function ScenePreview({ width, className, ...props }: ScenePreviewProps) {
  const bleed = props.bleed ?? 0;
  const size = pageSize(
    props.document,
    props.document.pages[props.pageIndex ?? 0] ?? props.document.pages[0],
  );
  const fullWidth = size.width + bleed * 2;
  const fullHeight = size.height + bleed * 2;
  const scale = width / fullWidth;
  return (
    <div
      className={className}
      style={{
        width,
        height: fullHeight * scale,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        style={{ transform: `scale(${scale})`, transformOrigin: "top left", pointerEvents: "none" }}
      >
        <SceneRenderer {...props} />
      </div>
    </div>
  );
}

export { CaptionFrameView, renderCaptions } from "./captions";
export { type DeviceLayer, DeviceParts, type DevicePartsElement } from "./device";
export { collectFontFaces, loadDocumentFonts } from "./fonts";
export { dashArray, GradientDef, ShapeSvg } from "./shape";
export {
  boxShadowCss,
  boxStyle,
  fontFamilyCss,
  joinFilters,
  LIST_MARKER_CSS,
  LIST_STEP_EM,
  paragraphStyle,
  textBoxStyle,
  textContentStyle,
  textRunStyle,
} from "./styles";
export { ColorMatrixFilter, StillFrame, type StillFrameProps } from "./video";
